/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import DynamicFieldsFragment from '@linc-technologies/linced-common/models/dynamic-field/base-fragment';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type MedicalConditionTypeEnum from '@linc-technologies/linced-common/enums/medical-condition-type';
import FieldAccessRoles from '@linc-technologies/linced-common/enums/roles/field-access';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import type PermissionsService from '@linc-technologies/linced-common/services/permissions';
import type SessionService from '@linc-technologies/linced-common/services/session';
import type StoreService from '@linc-technologies/linced-common/services/store';
import { rejectBy, uniq } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import MedicalAccess from 'linced-app/enums/medical-access';
import MedicalConditionType from 'linced-app/enums/medical-condition-type';
import type RolesService from 'linced-app/services/roles';

// Proto: people.MedicalCondition.
export default class MedicalCondition extends DynamicFieldsFragment {
  declare [Type]: 'people/medical-condition';

  // Services
  @service
  declare permissions: PermissionsService;

  @service
  declare roles: RolesService;

  @service
  declare session: SessionService;

  @service
  declare store: StoreService;

  // type denotes the type of medical condition.
  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof MedicalConditionTypeEnum> | 0;

  // priority determines the sort order for medical conditions.
  @attr('number', { defaultValue: 0 })
  declare priority: number;

  // description is a brief description of the medical condition.
  @attr('string', { defaultValue: '' })
  declare description: string;

  // startDate is the date from which this condition is considered current.
  @attr('string', { defaultValue: '' })
  declare startDate: string;

  // reviewDate is the date on which this condition is due to be reviewed.
  @attr('string', { defaultValue: '' })
  declare reviewDate: string;

  // endDate is the date on which this condition ceases to be current.
  @attr('string', { defaultValue: '' })
  declare endDate: string;

  // prescribedMedicationHeld flags whether the school holds prescribed
  // medication for this condition onsite.
  @attr('boolean', { defaultValue: false })
  declare prescribedMedicationHeld: boolean;

  // anaphylaxis flags whether this condition can result in an anaphylactic
  // reaction.
  @attr('boolean', { defaultValue: false })
  declare anaphylaxis: boolean;

  // highRisk flags whether this condition is high risk.
  @attr('boolean', { defaultValue: false })
  declare highRisk: boolean;

  // externalId allows this condition to be linked to an external record.
  @attr('string', { defaultValue: '' })
  declare externalId: string;

  // roleRequired determines the role required to view the medical condition
  @attr('number', { defaultValue: MedicalAccess.NONE })
  declare roleRequired: EnumValue<typeof MedicalAccess>;

  // medicationIds links the MedicalCondition to a set of Medications
  @array('string')
  declare medicationIds: StatefulArray<string>;

  // fields contains additional data about this condition, in dynamic fields.
  // map<string, FieldValue> fields = 100;

  // isCurrent determines whether this condition should be considered current.
  @computed('startDate', 'endDate')
  get isCurrent() {
    const today = moment().format('YYYY-MM-DD');
    const hasStarted = isEmpty(this.startDate) || this.startDate <= today;
    const hasEnded = !isEmpty(this.endDate) && this.endDate < today;
    return hasStarted && !hasEnded;
  }

  // typeName returns the enum label for the condition's type.
  @computed('type')
  get typeName() {
    return MedicalConditionType.getLabel(this.type);
  }

  // summary generates a text summary of this condition.
  @computed(
    'description',
    'anaphylaxis',
    'highRisk',
    'prescribedMedicationHeld',
    'fields.{core:subtype.value,core:autoinjectorHeldOnsite.value}',
    'session.school.jurisdiction',
  )
  get summary() {
    let summary = this.description;
    const extras = [];
    if (this.highRisk) {
      extras.push('High Risk');
    }
    if (this.anaphylaxis) {
      extras.push('Anaphylaxis');
    }
    if (this.prescribedMedicationHeld) {
      extras.push('Prescribed medication held onsite');
    }
    if (this.get('fields.core:autoinjectorHeldOnsite.value')) {
      // We'd ideally get the field label for this, but we may not have the
      // schema loaded - so we'll just fudge it from the jurisdiction.
      switch (this.get('session.school.jurisdiction')) {
        case Jurisdiction.NZ_MOE:
          extras.push('Epipen held onsite');
          break;

        default:
          extras.push('Adrenaline Autoinjector held onsite');
          break;
      }
    }
    if (!isEmpty(summary) && !isEmpty(extras)) {
      summary += ' - ';
    }
    summary += `${extras.join('; ')}`;
    return summary;
  }

  @computed('startDate', 'endDate', 'reviewDate')
  get dates() {
    let dateRange = '';
    let { startDate, endDate, reviewDate } = this;
    // Conform dates to DD/MM/YYYY if non-empty.
    if (!isEmpty(startDate)) {
      startDate = moment(startDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    }
    if (!isEmpty(endDate)) {
      endDate = moment(endDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    }
    if (!isEmpty(reviewDate)) {
      reviewDate = moment(reviewDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    }
    switch (true) {
      case !isEmpty(startDate) && !isEmpty(endDate):
        dateRange = `${startDate} - ${endDate}`;
        break;

      case !isEmpty(startDate):
        dateRange = `From ${startDate}`;
        break;

      case !isEmpty(endDate):
        dateRange = `Until ${endDate}`;
        break;
    }
    if (!isEmpty(reviewDate)) {
      if (isEmpty(dateRange)) {
        dateRange = `Review on ${reviewDate}`;
      } else {
        dateRange += ` (review on ${reviewDate})`;
      }
    }
    return dateRange;
  }

  @computed('type', 'anaphylaxis', 'highRisk')
  get isSerious() {
    const seriousTypes = [
      MedicalConditionType.SERIOUS_ALLERGY,
      MedicalConditionType.SERIOUS_MEDICAL_CONDITION,
    ];

    return (
      (seriousTypes as number[]).includes(this.type) ||
      this.anaphylaxis ||
      this.highRisk
    );
  }

  @computed('medicationIds.[]')
  get medications() {
    return rejectBy(
      uniq(this.medicationIds).map((medicationId) => {
        return this.store.peekRecord('medical/medication', medicationId);
      }),
      'deleted',
    );
  }

  @computed('fields.core:subtype.valueString')
  get subtype() {
    const subtypeField = this.fields['core:subtype'];
    if (subtypeField) {
      return subtypeField.get('valueString');
    }
    return '';
  }

  @computed('roleRequired', 'session.roles.[]')
  get userCanAccess() {
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }
    switch (this.roleRequired) {
      case MedicalAccess.NONE:
        return true;
      case MedicalAccess.STUDENT_MEDICAL:
        return this.roles.has(FieldAccessRoles.STUDENT_MEDICAL) as boolean;
      case MedicalAccess.STUDENT_SENSITIVE:
        return this.roles.has(FieldAccessRoles.STUDENT_SENSITIVE) as boolean;
    }
  }
}
